
import React, { createContext, useContext } from 'react';
import { useOnboarding } from '@/hooks/use-onboarding';

type OnboardingContextType = ReturnType<typeof useOnboarding>;

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Error boundary for OnboardingProvider
class OnboardingErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.warn('OnboardingProvider error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Provide fallback context
      const fallbackContext: OnboardingContextType = {
        isLoading: false,
        userData: null,
        onboardingComplete: false,
        resetOnboarding: () => console.warn('resetOnboarding called in error state'),
        loadUserData: () => console.warn('loadUserData called in error state'),
        saveUserData: () => console.warn('saveUserData called in error state')
      };
      
      return (
        <OnboardingContext.Provider value={fallbackContext}>
          {this.props.children}
        </OnboardingContext.Provider>
      );
    }
    return this.props.children;
  }
}

// Safe OnboardingProvider component
const OnboardingProviderComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('OnboardingProvider - Initializing');
  
  // Safe hook initialization
  let onboardingData: OnboardingContextType;
  
  try {
    // Check if React hooks are available
    if (typeof React === 'undefined' || !React.useState || !React.useEffect) {
      throw new Error('React hooks not available');
    }
    
    onboardingData = useOnboarding();
    console.log('OnboardingProvider - Hook data loaded successfully:', onboardingData);
  } catch (error) {
    console.error('OnboardingProvider - Error initializing hook:', error);
    // Provide comprehensive fallback data to prevent crash
    onboardingData = {
      isLoading: false,
      userData: null,
      onboardingComplete: false,
      resetOnboarding: () => {
        console.warn('OnboardingProvider - resetOnboarding called in fallback mode');
      },
      loadUserData: () => {
        console.warn('OnboardingProvider - loadUserData called in fallback mode');
      },
      saveUserData: () => {
        console.warn('OnboardingProvider - saveUserData called in fallback mode');
      }
    };
  }
  
  return (
    <OnboardingContext.Provider value={onboardingData}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <OnboardingErrorBoundary>
      <OnboardingProviderComponent>{children}</OnboardingProviderComponent>
    </OnboardingErrorBoundary>
  );
};

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    console.error('useOnboardingContext must be used within an OnboardingProvider');
    // Return fallback instead of throwing to prevent crashes
    return {
      isLoading: false,
      userData: null,
      onboardingComplete: false,
      resetOnboarding: () => console.warn('resetOnboarding called outside provider'),
      loadUserData: () => console.warn('loadUserData called outside provider'),
      saveUserData: () => console.warn('saveUserData called outside provider')
    };
  }
  return context;
};
